import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Badge,
  Table,
  Spinner,
  Alert,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import classNames from "classnames";
import SupplierService from "../../services/OrderingServices/SupplierService";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import toast from "react-hot-toast";

// Reusable InfoRow component matching CustomerDetail design
const InfoRow = ({ label, value, valueClass = "" }) => (
  <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
    <span className="text-muted" style={{ fontSize: "14px" }}>
      {label}:
    </span>
    <span className={`fw-medium ${valueClass}`} style={{ fontSize: "14px" }}>
      {value}
    </span>
  </div>
);

const SupplierDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [supplier, setSupplier] = useState(null);
  const [balance, setBalance] = useState(null);
  const [pendingPurchases, setPendingPurchases] = useState([]);
  const [activeTab, setActiveTab] = useState("1");

  const breadcrumbItems = [{ title: "Back To List", link: "/suppliers" }];

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setFetchError(null);
      const [supplierData, balanceData, purchasesData] = await Promise.all([
        SupplierService.detail(id),
        SupplierService.getSupplierBalance(id),
        SupplierService.getPendingPurchases(id),
      ]);

      setSupplier(supplierData);
      setBalance(balanceData);
      setPendingPurchases(purchasesData || []);
    } catch (error) {
      console.error("Error fetching supplier details:", error);
      setFetchError("Failed to load supplier data. Please try again.");
      toast.error("Failed to load supplier details");
    } finally {
      setLoading(false);
    }
  };

  const toggleTab = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  return (
    <Container fluid>
      <div className="page-content">
        <BreadCrumb
          pageTitle="Supplier"
          title="Supplier Detail"
          breadcrumbItems={breadcrumbItems}
        />

        {/* Error Alert */}
        {fetchError && (
          <Alert color="danger" className="mb-4">
            {fetchError}
          </Alert>
        )}

        {loading ? (
          <Card className="shadow-sm border-0">
            <CardBody className="text-center py-5">
              <Spinner color="primary" />
              <p className="mt-3 text-muted">Loading supplier data...</p>
            </CardBody>
          </Card>
        ) : (
          <>
            {/* Main Supplier Profile Card */}
            <Card className="shadow-sm border-0 mb-4">
              <CardBody className="p-4">
                <Row>
                  {/* Left Column - Avatar and Basic Info */}
                  <Col lg={4} className="text-center mb-4 mb-lg-0">
                    <div className="position-relative d-inline-block">
                      <div
                        className="d-inline-flex align-items-center justify-content-center bg-light rounded-circle"
                        style={{
                          width: "150px",
                          height: "150px",
                          border: "4px solid #f0f0f0",
                        }}
                      >
                        <i
                          className="ri-building-line"
                          style={{ fontSize: "70px", color: "#002538" }}
                        ></i>
                      </div>
                    </div>

                    <h4 className="mt-3 mb-1">{supplier?.name}</h4>
                    <Badge
                      color="info"
                      className="px-3 py-2"
                      style={{ fontSize: "12px" }}
                    >
                      Supplier
                    </Badge>
                  </Col>

                  {/* Right Column - Detailed Information */}
                  <Col lg={8}>
                    <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                      <h5 className="mb-0">Supplier Information</h5>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() =>
                          navigate("/suppliers/payment", {
                            state: { supplierId: parseInt(id) },
                          })
                        }
                      >
                        <i className="ri-money-dollar-circle-line me-1"></i> Add
                        Payment
                      </button>
                    </div>

                    <Row>
                      <Col md={6}>
                        <InfoRow
                          label="Contact Person"
                          value={supplier?.contactPerson || "N/A"}
                        />
                        <InfoRow
                          label="Phone Number"
                          value={supplier?.phone || "N/A"}
                        />
                        <InfoRow
                          label="Email"
                          value={supplier?.email || "N/A"}
                        />
                        <InfoRow
                          label="Address"
                          value={supplier?.address || "N/A"}
                        />
                        <InfoRow
                          label="City"
                          value={supplier?.city || "N/A"}
                        />
                        <InfoRow
                          label="PAN Number"
                          value={supplier?.panNumber || "N/A"}
                        />
                      </Col>
                      <Col md={6}>
                        
                        <InfoRow
                          label="Total Billed"
                          value={`Rs. ${(balance?.totalBillAmount || 0).toLocaleString()}`}
                          valueClass="fw-bold"
                        />
                        <InfoRow
                          label="Total Paid"
                          value={`Rs. ${(balance?.totalPaidAmount || 0).toLocaleString()}`}
                          valueClass="text-success fw-bold"
                        />
                        <InfoRow
                          label="Total Returns"
                          value={`Rs. ${(balance?.totalReturnAmount || 0).toLocaleString()}`}
                          valueClass="text-warning fw-bold"
                        />
                        <InfoRow
                          label="Outstanding Balance"
                          value={`Rs. ${(balance?.outstandingBalance || 0).toLocaleString()}`}
                          valueClass={`fw-bold ${balance?.outstandingBalance > 0 ? "text-danger" : "text-success"}`}
                        />
                        <InfoRow
                          label="Unpaid Purchases"
                          value={balance?.unpaidPurchaseCount ?? pendingPurchases.length}
                          valueClass={`fw-bold ${(balance?.unpaidPurchaseCount || pendingPurchases.length) > 0 ? "text-danger" : ""}`}
                        />
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {/* Tabs Section */}
            <div className="w-100">
              <Row>
                <Col lg={12}>
                  <Nav
                    pills
                    className="animation-nav profile-nav gap-2 gap-lg-3"
                    role="tablist"
                  >
                    <NavItem>
                      <NavLink
                        href="#"
                        className={classNames({ active: activeTab === "1" })}
                        onClick={() => toggleTab("1")}
                        title="Pending Purchases"
                      >
                        <i className="ri-file-list-3-line d-inline-block d-md-none"></i>{" "}
                        <span
                          className="d-none d-md-inline-block fw-bold"
                          style={{ color: "rgb(0,37,56)" }}
                        >
                          Pending Purchases
                        </span>
                      </NavLink>
                    </NavItem>
                  </Nav>

                  <TabContent activeTab={activeTab} className="pt-4 text-muted w-100">
                    <TabPane tabId="1">
                      <Card className="shadow-sm border-0">
                        <div className="card-header bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                          <h6 className="card-title fw-bold mb-0">
                            Pending Purchases
                          </h6>
                          <Badge color="soft-danger" pill className="fs-12">
                            {pendingPurchases.length} pending
                          </Badge>
                        </div>
                        <CardBody className="p-0">
                          <div className="table-responsive">
                            <Table className="align-middle table-nowrap mb-0 table-hover">
                              <thead className="table-light">
                                <tr>
                                  <th className="px-3 text-muted small text-uppercase">Date</th>
                                  <th className="px-3 text-muted small text-uppercase">Reference No</th>
                                  <th className="px-3 text-muted small text-uppercase">Voucher</th>
                                  <th className="px-3 text-muted small text-uppercase text-end">Total</th>
                                  <th className="px-3 text-muted small text-uppercase text-end">Paid</th>
                                  <th className="px-3 text-muted small text-uppercase text-end">Due</th>
                                  <th className="px-3 text-muted small text-uppercase text-center">Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {pendingPurchases.length > 0 ? (
                                  pendingPurchases.map((p) => (
                                    <tr key={p.purchaseId}>
                                      <td className="px-3">
                                        <span className="small fw-medium">{p.purchaseDate_Nep}</span>
                                        <small className="text-muted d-block">
                                          {new Date(p.purchaseDate).toLocaleDateString()}
                                        </small>
                                      </td>
                                      <td className="px-3 fw-medium text-dark">{p.referenceNo}</td>
                                      <td className="px-3">{p.voucherNo || "—"}</td>
                                      <td className="px-3 text-end">Rs. {p.totalAmount?.toLocaleString()}</td>
                                      <td className="px-3 text-end text-success">Rs. {p.amountPaid?.toLocaleString()}</td>
                                      <td className="px-3 text-end fw-bold text-danger">Rs. {p.amountDue?.toLocaleString()}</td>
                                      <td className="px-3 text-center">
                                        <Badge
                                          color={p.paymentStatus === "Pending" ? "warning" : "success"}
                                          style={{ fontSize: "10px" }}
                                        >
                                          {p.paymentStatus}
                                        </Badge>
                                      </td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td colSpan="7" className="text-center py-4 text-muted">
                                      <i className="ri-checkbox-circle-line fs-24 text-success d-block mb-1"></i>
                                      No pending purchases for this supplier.
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </Table>
                          </div>
                        </CardBody>
                      </Card>
                    </TabPane>
                  </TabContent>
                </Col>
              </Row>
            </div>
          </>
        )}
      </div>
    </Container>
  );
};

export default SupplierDetail;
